import React, { Fragment, useEffect, useMemo, } from 'react';
import { Button, } from 'reactstrap';
import { useToggle } from 'react-use';
import { orderBy, chunk, isEmpty, omit, sumBy, groupBy, findKey, } from 'lodash';
import numeral from 'numeral';
import { useBoolean } from 'react-use';
import fileDownload from 'js-file-download';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import qs from 'qs';
import { format as formatDate } from 'date-fns';
import { toast } from 'react-toastify';
import Toggle from 'react-toggle';
import { useCounter, } from 'react-use';

import env from '../../env';
import firebase, { functions } from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import { boxSizes, } from '../../shared/config';
import { batch, getCollectionData, } from '../../shared/firebase';
import { fields, computeInventory, priceTaxTypes, } from '../../shared/models/product';
import { newId, createProductsWithSkuCheck } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionFetch from '../hooks/useCollectionFetch';
import useQueryParams from '../hooks/useQueryParams';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import usePagination from '../hooks/usePagination';
import Page from '../hocs/Page';
import ModelFormModal from '../modals/ModelFormModal';
import ProductInventoryHistoryModal from '../modals/ProductInventoryHistoryModal';
import ProductsBatchUpdateModal from '../modals/ProductsBatchUpdateModal';
import ProductsBatchDeleteModal from '../modals/ProductsBatchDeleteModal';
import Field from '../Field';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ImportButton from '../ImportButton';
import ProgressButton from '../ProgressButton';
import QuerySortLabel from '../QuerySortLabel';
import QueryBoolean from '../QueryBoolean';
import QueryInput from '../QueryInput';
import Pagination from '../Pagination';
import DataToggle from '../DataToggle';
import ProductsTabs from '../ProductsTabs';
import SimpleLoading from '../SimpleLoading';
import ProductTagsDisplay from '../ProductTagsDisplay';

const countPerPage = 100;
const { keys, values, entries } = Object;
const db = firebase.firestore();
const sortIterateees = {
  id: _ => _.createdAt.toDate(),
};

export default Page(function Products (props) {
  const { user, tenant, history, } = props;
  const {
    showsAll,
    showsInventories,
    sort = { field: 'id', direction: 'desc', },
    keyword,
  } = useQueryParams();
  const isSearching = !isEmpty(keyword);
  const [componentVersion, { inc: updateComponentVersion }] = useCounter(0);
  const [showsAmazonProductAddModal, toggleAmazonProductAddModal] = useBoolean(false);
  const [showsBatchUpdateModal, toggleBatchUpdateModal] = useToggle(false);
  const [showsBatchDeleteModal, toggleBatchDeleteModal] = useToggle(false);
  const productsRef = tenant.ref.collection('products');
  const otherLocations = useCollectionSubscription(tenant.ref.collection('otherLocations').orderBy('createdAt'), [tenant.id]);
  const { items: productChunks, isLoading: isLoadingProductChunks } = useCollectionSubscription(tenant.ref.collection('productChunks'), [tenant.id], { detail: true, initialItems: null, });
  const chunkProducts = useMemo(() => orderBy(productChunks?.flatMap(_ => entries(omit(_, ['id', 'ref', 'exists'])).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('products').doc(k), ...v, })).map(_ => ({ ..._, fullId: idWithTenant(_.ref) }))), _ => numeral(_.id).value(), 'desc'), [tenant, productChunks]);

  // NOTE: 絞り込み
  const filterChunkProducts = () => {
    let filteredChunkProducts = chunkProducts;
    filteredChunkProducts = filteredChunkProducts.filter(_ => !_.isAmazonProduct && !_.isCase && !_.isSet);
    if(isSearching) {
      if(!isEmpty(keyword)) {
        filteredChunkProducts = filteredChunkProducts.filter(p => ['fullId', 'name', 'sku', 'asin', 'barcodeInfo'].some(_ => p[_]?.includes(keyword)));
      }
    }
    return filteredChunkProducts;
  };

  const filteredChunkProducts = useMemo(() => filterChunkProducts(), [chunkProducts, keyword]);
  const pagination = usePagination({ countPerPage, totalCount: filteredChunkProducts.length, });
  const { pageNumber, offset, } = pagination;
  const { items: products, isLoading } = useDocumentsFetch(productChunks != null && filteredChunkProducts.slice(offset, offset + countPerPage).map(_ => _.ref), [tenant, filteredChunkProducts, pageNumber, componentVersion], { detail: true, initialItems: null });
  const caseProducts = useCollectionSubscription(productsRef.where('isCase', '==', true), [tenant.id]);
  const caseUsingProductIds = caseProducts.flatMap(_ => _.items || []).map(_ => _.productId);
  // セット品の構成品も削除させない（削除するとセット品の在庫が突然0になるため）
  const setProducts = useCollectionSubscription(productsRef.where('isSet', '==', true), [tenant.id]);
  const setUsingProductIds = setProducts.flatMap(_ => _.items || []).map(_ => _.productId);
  const normalProductsGroupedBySku = groupBy(chunkProducts.filter(_ => !_.isAmazonProduct && !_.isCase && !_.isSet), 'sku');
  const caseInventoryByProductId = caseProducts.reduce((x, caseProduct) => {
    const caseGoodInventory = (caseProduct.goodInventoryIds || []).length;
    return (caseProduct.items || []).reduce((x, item) => ({
      ...x,
      [item.productId]: (x[item.productId] || 0) + caseGoodInventory * item.quantity,
    }), x);
  }, {});
  const receivingRequestsRef = tenant.ref.collection('receivingRequests');
  const shippingRequestsRef = tenant.ref.collection('shippingRequests');
  const { items: receivingRequests, isLoading: isLoadingReceivingRequests } = useCollectionFetch(showsInventories === '1' && products != null && receivingRequestsRef.where('status', '!=', 'received'), [showsInventories, products != null, tenant.id], { detail: true });
  const { items: shippingRequests, isLoading: isLoadingShippingRequests } = useCollectionFetch(showsInventories === '1' && products != null && shippingRequestsRef.where('status', 'not-in', ['shipped', 'discarded', 'cancelled']).orderBy('status'), [showsInventories, products != null, tenant.id], { detail: true });
  const isLoadingSomeRequestData = isLoadingReceivingRequests || isLoadingShippingRequests;
  const receivingRequestItemsGroupedByProductId = groupBy(receivingRequests.map(_ => _.items || []).flat(), 'productId');
  const shippingRequestItemsGroupedByProductId = groupBy(shippingRequests.map(_ => _.items || []).flat(), 'productId');
  const productItems = (products || []).map((product) => {
    const { id } = product;
    const receivingRequestItems = receivingRequestItemsGroupedByProductId[id] || [];
    const receivingCount = sumBy(receivingRequestItems, 'quantity')
    const { goodInventory, ngInventory, waitingToShippingGoodCount, waitingToShippingNgCount, goodInventoryWithoutAllocation, ngInventoryWithoutAllocation } = computeInventory(product, shippingRequests);
    return {
      ...product,
      receivingRequestItems,
      receivingCount,
      goodInventory,
      ngInventory,
      waitingToShippingGoodCount,
      waitingToShippingNgCount,
      goodInventoryWithoutAllocation,
      ngInventoryWithoutAllocation,
    };
  });
  const sortedProductItems = orderBy(productItems, (sortIterateees[sort.field] || sort.field), sort.direction);
  const hiddenCount = showsAll === '1' ? 0 : sortedProductItems.filter(_ => _.hides).length;
  const [lastProduct] = orderBy(chunkProducts, _ => parseInt(_.id.replace(/\D/g, ''), 10), 'desc');
  const newRef = !isLoadingProductChunks && productsRef.doc(newId(lastProduct));
  const onClickDownloadSampleCsv = () => {
    const data = [
      {
        商品名: '本',
        SKU: 'sku_123456',
        JANまたはバーコード情報: '1234123412345',
        品名: '書籍',
        ポスト便希望: 'はい',
        販売価格: 1100,
        税区分: '税込',
      },
    ];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, '商品マスタ_サンプル.csv');
  };
  const validate = async (rows, row, i) => {
    const {
      商品名: name,
      SKU: sku,
      JANまたはバーコード情報: barcodeInfo,
      品名: itemName,
      ポスト便希望: _requestsNeko,
      販売価格: _price = '',
      税区分: priceTaxTypeLabel = '',
    } = row;
    const requestsNeko = _requestsNeko === 'はい';
    const price = isEmpty(_price) ? null : Number(_price.replace(/,/g, ''));
    const priceTaxType = price == null ? null : isEmpty(priceTaxTypeLabel) ? 'included' : findKey(priceTaxTypes, { label: priceTaxTypeLabel });
    const otherSkus = [...chunkProducts.map(_ => _.sku), ...rows.slice(0, i).map(_ => _.SKU)];
    const errors = [
      !name && '商品名は必須です',
      otherSkus.includes(sku) && 'SKUが他に存在します',
      sku.match(/\s/) && 'SKUに空白やタブ等が含まれています',
      (itemName && itemName.length > 16) && '品名は16文字以内です',
      price != null && !(Number.isInteger(price) && price >= 0) && '販売価格は0以上の整数です',
      price != null && !priceTaxType && '税区分は「税込」または「税抜」です',
    ];
    return {
      source: row,
      errors: errors.filter(_ => _),
      name,
      sku,
      barcodeInfo,
      itemName,
      requestsNeko,
      price,
      priceTaxType,
    }
  };
  const processRows = async (rows) => {
    const allRows = rows;
    const validatedRows = (await chunk(rows.filter(_ => _['商品名']), 10)
      .reduce(async (x, rows, chunkIndex) => {
        const prevs = await x;
        return [...prevs, (await Promise.all(rows.map((row, i) => validate(allRows, row, chunkIndex * 10 + i))))];
      }, Promise.resolve([])))
      .flat();
    if(validatedRows.filter(_ => !isEmpty(_.errors)).length > 0) {
      const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
      const content = encoder.encode(unparseCsv(validatedRows.map((row) => {
        return {
          ...row.source,
          エラー内容: row.errors.join('\n'),
        };
      })));
      fileDownload(content, 'エラー結果.csv');
      throw new Error();
    } else {
      const rowsForCreate = validatedRows.map((row, i) => ({
        productId: (parseInt(newId(lastProduct), 10) + i).toString(),
        sku: row.sku,
        data: {
          ...omit(row, ['source', 'errors']),
          itemName: row.itemName || row.name.slice(0, 10),
          goodInventory: 0,
          ngInventory: 0,
          isAmazonProduct: false,
          shippingSize: null,
          createdAt: new Date(),
        },
      }));
      await createProductsWithSkuCheck(db, productsRef, rowsForCreate);
    }
  };
  const renderFormModalFooterContent = (base, { onSubmit, isUnsubmittable, isSubmitting }) => {
    const onClickWithReceivingRequest = async (event) => {
      await onSubmit(event);
      history.push(`/receivingRequests?newProductId=${newRef.id}`);
    };
    const onClickWithReceivingRequestAndShippingRequest = async (event) => {
      await onSubmit(event);
      const [lastReceivingRequest] = orderBy(await getCollectionData(receivingRequestsRef), _ => parseInt(_.id.replace(/\D/g, ''), 10), 'desc');
      await receivingRequestsRef.doc('W' + newId(lastReceivingRequest)).set({
        items: [{ productId: newRef.id, quantity: 1, goodQuantity: 0, ngQuantity: 0 }],
        checkNote: null,
        documentNumber: null,
        pendingItems: [],
        receivingOptionValues: [],
        setNote: null,
        status: 'initial',
        productIds: [newRef.id],
        createdAt: new Date(),
      });
      toast.success('入荷依頼を作成しました');
      history.push(`/shippingRequests?${qs.stringify({ newItems: [{ productId: newRef.id, goodQuantity: 1, }], newType: 'normal' })}`);
    };
    return (
      <Fragment>
        {base}
        <Button className="save" type="submit" color="primary" onClick={onClickWithReceivingRequest} disabled={isUnsubmittable || isSubmitting}>入荷依頼する</Button>
        <Button className="save" type="submit" color="primary" onClick={onClickWithReceivingRequestAndShippingRequest} disabled={isUnsubmittable || isSubmitting}>入出荷依頼する</Button>
      </Fragment>
    );
  };
  useEffect(() => {
    user.ref.set({ preferences: { productsTab: '/products', } }, { merge: true });
  }, []);

  return (
    <div className="products container-fluid">
      <div className="p-4 bg-white my-4">
        <ProductsTabs activeTab="products" products={products || []} shippingRequests={shippingRequests} tenant={tenant} />
        <div className="d-flex justify-content-start align-items-end mb-2 gap-1">
          <QueryInput width={300} paramName="keyword" label="SKU、商品名、ASIN、JAN、商品IDで検索" />
          <div className="ml-2 d-flex gap-2 align-items-center">
            <QueryBoolean paramName="showsAll" label="非表示も表示" defaultValue={'0'} />
            <QueryBoolean paramName="showsInventories" label="在庫を表示" defaultValue={'0'} />
            {hiddenCount > 0 && <div className="text-warning">このページでは{hiddenCount}件が非表示になっています</div>}
          </div>
        </div>
        <div className="d-flex justify-content-end mb-1 gap-1 align-items-end">
          <Pagination {...pagination} />
          <Button onClick={onClickDownloadSampleCsv}>
            <span className="fas fa-download mr-1" />
            サンプルCSVダウンロード
          </Button>
          <ImportButton processRows={processRows} disabled={isLoadingProductChunks} />
          <Button onClick={toggleBatchUpdateModal.bind(null, true)} disabled={isLoadingProductChunks}>
            <span className="fas fa-edit mr-1" />
            CSV一括更新
          </Button>
          <Button color="danger" onClick={toggleBatchDeleteModal.bind(null, true)} disabled={isLoadingProductChunks}>
            <span className="fas fa-trash mr-1" />
            CSV一括削除
          </Button>
          <AddButton
            itemRef={newRef}
            disabled={isLoadingProductChunks}
            processValues={_ => ({ ..._, itemName: _.itemName || _.name.slice(0, 10), isAmazonProduct: false, shippingSize: null, })}
            beforeSave={async ({ id, ...values }) => {
              const data = { ...omit({ ...values, itemName: values.itemName || values.name.slice(0, 10), isAmazonProduct: false, shippingSize: null, }, ['id', 'ref']), createdAt: new Date(), };
              await createProductsWithSkuCheck(db, productsRef, [{ productId: id, sku: values.sku, data }]);
              return { skipDefaultSet: true };
            }}
            FormModal={ModelFormModal}
            formProps={{
              title: '商品 追加',
              // NOTE: タグ機能実装まで、通常商品では隠すことにした。
              fields: omit(fields({ tenant, otherProducts: chunkProducts }), 'productTagIds'),
              renderFooterContent: renderFormModalFooterContent,
            }}
          />
        </div>
        <div className="mt-1 overflow-auto">
          {
            sortedProductItems.length > 0 ? (
              <table className="table table-sm table-bordered sticky-table">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: 150 }}>
                      SKU
                      <br />
                      登録日
                      <div className="small">商品ID</div>
                    </th>
                    <th style={{ minWidth: 200 }}>
                      商品名
                      <br />
                      品名
                      <br />
                      サイズ
                    </th>
                    <th style={{ minWidth: 200 }}>
                      JANまたはバーコード情報
                    </th>
                    <th style={{ minWidth: 100 }}>
                      販売価格
                    </th>
                    {
                      showsInventories === '1' && (
                        <Fragment>
                          <th style={{ minWidth: 60 }}>
                            在庫
                          </th>
                          <th style={{ minWidth: 90 }}>
                            販売不可在庫
                          </th>
                          <th style={{ minWidth: 180 }}>入出荷作業中</th>
                        </Fragment>
                      )
                    }
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    (showsAll === '1' ? sortedProductItems : sortedProductItems.filter(_ => !_.hides)).map((productItem) => {
                      const { id, ref, isAmazonProduct, name, sku, barcodeInfo, itemName, requestsNeko = false, goodInventory = 0, ngInventory = 0, fnsku, asin, receivingCount, waitingToShippingGoodCount, waitingToShippingNgCount, goodInventoryWithoutAllocation, ngInventoryWithoutAllocation, createdAt, shippingSize, hides = false, otherLocationInventories = {}, price, priceTaxType, } = productItem;
                      const isUsedByCase = caseUsingProductIds.includes(id);
                      const isUsedBySet = setUsingProductIds.includes(id);
                      const beforeDelete = async () => {
                        const [receivingRequest] = await getCollectionData(tenant.ref.collection('receivingRequests').where('productIds', 'array-contains', id).limit(1));
                        const [shippingRequest] = await getCollectionData(tenant.ref.collection('shippingRequests').where('productIds', 'array-contains', id).limit(1));
                        if(receivingRequest || shippingRequest) {
                          toast.error('一度でも入出荷があった商品は削除できません。');
                          return false;
                        }
                      };

                      return (
                        <tr key={id}>
                          <td>
                            <div>
                              <span className="font-weight-bold">SKU:</span> {sku}
                            </div>
                            <div>{formatDate(createdAt.toDate(), 'yyyy/MM/dd')}</div>
                            <div className="small">{idWithTenant(ref)}</div>
                            {requestsNeko && <div className="badge badge-warning">ポスト便希望</div>}
                            <ProductTagsDisplay product={productItem} productTags={[]} />
                            {user.dev && <a href={`https://console.firebase.google.com/u/0/project/${env('FIREBASE_PROJECT_ID')}/firestore/data/~2Ftenants~2F${tenant.id}~2Fproducts~2F${id}`} target="_blank">F</a>}
                          </td>
                          <td>
                            <div>
                              {name}
                            </div>
                            <div className="font-weight-bold">
                              {itemName}
                            </div>
                            <div className="font-weight-bold">
                              {boxSizes[shippingSize]?.label}
                            </div>
                          </td>
                          <td>
                            <div>
                              <span className="font-weight-bold">JAN:</span> {barcodeInfo}
                            </div>
                            {isAmazonProduct && <div className="font-weight-bold">ASIN: {asin}</div>}
                            {isAmazonProduct && <div className="font-weight-bold">FNSKU: {fnsku}</div>}
                          </td>
                          <td className="text-right">
                            {
                              price != null && (
                                <Fragment>
                                  <div>{numeral(price).format('0,0')}円</div>
                                  <div className="small text-muted">{priceTaxTypes[priceTaxType]?.label}</div>
                                </Fragment>
                              )
                            }
                          </td>
                          {
                            showsInventories === '1' && (
                              <Fragment>
                                <td className="text-right">
                                  <SimpleLoading isLoading={isLoadingSomeRequestData}>
                                    <div>{numeral(goodInventory).format('0,0')}</div>
                                    {
                                      otherLocations.filter(_ => otherLocationInventories[_.sortKey]).map(_ => (
                                        <div key={_.id} className="small text-muted">{_.name}: {numeral(otherLocationInventories[_.sortKey]).format('0,0')}</div>
                                      ))
                                    }
                                    {caseInventoryByProductId[id] > 0 && <div className="small text-muted text-nowrap">ケース内 {numeral(caseInventoryByProductId[id]).format('0,0')}</div>}
                                  </SimpleLoading>
                                </td>
                                <td className="text-right">
                                  <SimpleLoading isLoading={isLoadingSomeRequestData}>{numeral(ngInventory).format('0,0')}</SimpleLoading>
                                </td>
                                <td>
                                  <SimpleLoading isLoading={isLoadingSomeRequestData}>
                                    <table className="mb-0 table table-sm table-borderless">
                                      <tbody>
                                        <tr>
                                          <th>入荷依頼中</th>
                                          <td className="text-right">{numeral(receivingCount).format('0,0')}</td>
                                        </tr>
                                        <tr>
                                          <th>出荷依頼中</th>
                                          <td className="text-right">{numeral(waitingToShippingGoodCount).format('0,0')}</td>
                                        </tr>
                                        <tr>
                                          <th>返送・廃棄依頼中</th>
                                          <td className="text-right">{numeral(waitingToShippingNgCount).format('0,0')}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </SimpleLoading>
                                </td>
                              </Fragment>
                            )
                          }
                          <td>
                            <div className="d-flex flex-column gap-1 text-nowrap">
                              <EditButton size="sm" itemRef={ref} FormModal={ModelFormModal} formProps={{ title: '商品 編集', fields: omit(fields({ tenant, }), ['sku', 'productTagIds']) }} onFinish={updateComponentVersion} />
                              <DeleteButton size="sm" item={productItem} itemRef={ref} disabled={isUsedByCase || isUsedBySet || [goodInventory, ngInventory, receivingCount, waitingToShippingGoodCount, waitingToShippingNgCount].some(_ => _ !== 0)} beforeDelete={beforeDelete} onFinish={updateComponentVersion} />
                              <DataToggle data={productItem} fieldName="hides" label="非表示" inputProps={{ style: { transform: 'initial' }, }} labelProps={{ className: 'small', }} onFinish={updateComponentVersion} />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                {
                  (isLoading || isLoadingProductChunks) ? (
                    <span className="fas fa-spin fa-spinner" />
                  ) : (
                    <span>商品は未登録です</span>
                  )
                }
              </div>
            )
          }
        </div>
      </div>
      {
        showsBatchUpdateModal && (
          <ProductsBatchUpdateModal
            isOpen
            onClickClose={_ => toggleBatchUpdateModal(false)}
            onFinish={updateComponentVersion}
            productsGroupedBySku={normalProductsGroupedBySku}
            exportTargetProducts={filteredChunkProducts}
            showsInventoryBuffer={!fields({ tenant }).inventoryBuffer.hidden()}
          />
        )
      }
      {
        showsBatchDeleteModal && (
          <ProductsBatchDeleteModal
            isOpen
            onClickClose={_ => toggleBatchDeleteModal(false)}
            onFinish={updateComponentVersion}
            tenant={tenant}
            productsGroupedBySku={normalProductsGroupedBySku}
            componentProductIds={[...caseUsingProductIds, ...setUsingProductIds]}
          />
        )
      }
    </div>
  );
});
